import { type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableCell,
  TableHeadCell,
} from "@/components/ui/table-shell";

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: "line" | "block" | "circle";
}

export function Skeleton({
  className,
  variant = "line",
  ...props
}: SkeletonProps) {
  return (
    <div
      className={cn(
        "animate-pulse bg-surface-2",
        variant === "line" && "h-3 rounded-md",
        variant === "block" && "rounded-xl",
        variant === "circle" && "rounded-full",
        className,
      )}
      {...props}
    />
  );
}

export interface TableSkeletonProps {
  columns: string;
  columnCount: number;
  rows?: number;
  className?: string;
}

export function TableSkeleton({
  columns,
  columnCount,
  rows = 6,
  className,
}: TableSkeletonProps) {
  return (
    <TableShell className={className}>
      <TableHeadRow columns={columns}>
        {Array.from({ length: columnCount }).map((_, i) => (
          <TableHeadCell key={i}>
            <Skeleton className="h-2 w-14" />
          </TableHeadCell>
        ))}
      </TableHeadRow>

      {Array.from({ length: rows }).map((_, rowIdx) => (
        <TableRow key={rowIdx} columns={columns}>
          {Array.from({ length: columnCount }).map((__, colIdx) => (
            <TableCell key={colIdx}>
              <Skeleton
                className={cn(
                  "h-3.5",
                  colIdx === 0 && "w-4/5",
                  colIdx === 1 && "w-3/4",
                  colIdx === 2 && "w-2/3",
                  colIdx > 2 && "w-1/2",
                )}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableShell>
  );
}

export function CardGridSkeleton({
  cards = 4,
  className,
}: {
  cards?: number;
  className?: string;
}) {
  return (
    <div className={cn("grid grid-cols-1 gap-4 lg:grid-cols-2", className)}>
      {Array.from({ length: cards }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
            <Skeleton className="h-7 w-16 rounded-full" />
          </CardHeader>
          <CardContent className="flex flex-col gap-3.5">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-[92%]" />
            <Skeleton className="h-3.5 w-[78%]" />
            <div className="mt-1 flex justify-end">
              <Skeleton className="h-8 w-28 rounded-lg" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function FormSkeleton({ lines = 6 }: { lines?: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <Skeleton className="h-2 w-24" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}

export function PanelSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("rounded-xl border border-line bg-surface p-4", className)}
    >
      <div className="flex flex-col gap-2.5">
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-[84%]" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}
